import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { v4 as uuid } from "uuid";
import queryString from "query-string";
import firebase from "firebase/compat/app";

import { Typography } from "@mui/material";
import Loader from "../../../components/loader/loader";
import ProjectHeader from "./components/project-header";
import BasicInfo from "./basic-info/basic-info";
import ProjectHealthNew from "./project-health/project-health";
import ProjectAssets from "./projects-assets/projects-assets";
import AddLinkModal from "./projects-assets/linkedAssets/addLinkModal/addLinkModal";
import CustomTabs from "../../../components/custom-tabs/custom-tabs";
import { ProjectDetailsStyled } from "./project-details.styles";

import useCompanies from "../../../hooks/useCompanies";
import { fetchLinkedProjects } from "../../../services/projectsServices";

import {
  projectsCollection,
  projectGroupCollection,
} from "../../../services/firebase";
import { useAuth } from "../../../context/AuthContext";
import {
  isSomething,
  formatNumber,
  formatNumberSafe,
} from "../../../utils/utils";
import {
  getDefaultProjectDetails,
  PROJECT_DETAILS_TAB_LIST,
} from "./project-details.utils";

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { search } = useLocation();
  const apiKey = React.useRef(uuid());
  const { isGroupView } = queryString.parse(search);

  const isGroup = isGroupView === "true";
  const [activeTab, setActiveTab] = useState("basic-info");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);

  const { userPermissions } = useAuth();
  const isGeneralOrPM = ["General", "Project Manager"].includes(
    userPermissions
  );

  const {
    data: projectDetails,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [isGroup ? "projectGroup" : "project", id, apiKey],
    queryFn: async () => {
      if (isGroup) {
        const doc = await projectGroupCollection.doc(id).get();
        if (!doc.exists) throw new Error("Project group not found");
        return { ...doc.data(), id: doc.id, firestoreId: doc.id };
      } else {
        const doc = await projectsCollection.doc(id).get();
        if (!doc.exists) throw new Error("Project not found");
        return { ...doc.data(), id: doc.id, firestoreId: doc.id };
      }
    },
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });

  const projectData = {
    ...getDefaultProjectDetails(projectDetails),
    linkedAssets: projectDetails?.linkedAssets || [],
  };

  const { companies } = useCompanies();

  const companyData = companies?.reduce((acc, company) => {
    acc[company.companyID] = company.companyName;
    return acc;
  }, {});

  const linkedAssets = projectData.linkedAssets || [];

  const form = useForm({
    defaultValues: projectData,
  });

  const { reset } = form;

  useEffect(() => {
    if (isSomething(projectDetails)) {
      reset(projectData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails]);

  const materialsData = projectDetails?.materials || [];

  if (isLoading) {
    return <Loader absolute />;
  }

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = form.getValues();
      const updatedClient = formData.client;

      // Handle start date formatting
      const startDate = formData.start
        ? dayjs(formData.start).format("YYYY-MM-DD")
        : null;

      const invoiceDate = formData.invoiceDate
        ? dayjs(formData.invoiceDate).format("YYYY-MM-DD")
        : null;

      // Format numeric fields
      const formattedServiceValue = formatNumber(
        parseFloat(formData.serviceValue.replace(/,/g, "")) || 0
      );

      const formattedSchedule =
        formData?.schedule?.map((item) => ({
          value: formatNumber(parseFloat(item.value.replace(/,/g, "")) || 0),
          month: item.month,
        })) || [];
      const currentDate = dayjs().format("YYYY-MM-DD");

      // Prepare updated project/group data
      const updatedProjectData = {
        projectName: formData.projectName,
        isGrouped: formData.isGrouped,
        isNonBillable: formData.isNonBillable,
        state: formData.state,
        addToExistingProject: formData.addToExistingProject,
        source: formData.source,
        client: updatedClient,
        serviceValue: formattedServiceValue,
        projectLead: formData.projectLead,
        start: startDate,
        ...(["Complete", "Cancelled"].includes(formData?.state) && {
          end: currentDate,
        }),
        // teamList: formData.teamList,
        contact: formData.contact,
        probability: formData.probability,
        // serviceGroups: formData.serviceGroups,
        schedule: formattedSchedule,
        projectDescription: formData.projectDescription,
        invoiceValue: formatNumberSafe(formData.invoiceValue) || "",
        invoiceDate: invoiceDate || "",
        invoiceType: formData.invoiceType || "",
      };

      if (isGroup) {
        // Update project group in projectGroupCollection
        await projectGroupCollection.doc(id).update(updatedProjectData);

        // Update linked projects if client or other group-level fields have changed
        const linkedProjects = await fetchLinkedProjects(id);
        const batch = firebase.firestore().batch();

        linkedProjects.forEach((project) => {
          const projectRef = projectsCollection.doc(project.id);

          // Update only fields that should propagate to linked projects
          batch.update(projectRef, {
            client: updatedClient,
            addToExistingProject: id, // Ensure linkage is maintained
          });
        });

        await batch.commit();
      } else {
        // Update single project in projectsCollection
        await projectsCollection.doc(id).update(updatedProjectData);
      }

      // Call any additional custom logic required after save
      refetch();
      setIsEditMode(false);

      console.log("Project updated successfully:", updatedProjectData);
    } catch (error) {
      console.error("Error updating project:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLink = async (newLink) => {
    try {
      const updatedLinks = [
        ...(projectData.linkedAssets || []),
        { ...newLink, id: uuid() },
      ];

      if (isGroupView) {
        // Save link in project group
        await projectGroupCollection
          .doc(id)
          .update({ linkedAssets: updatedLinks });
      } else {
        // Save link in individual project
        await projectsCollection.doc(id).update({ linkedAssets: updatedLinks });
      }

      refetch(); // Refresh data after saving the link
      console.log("Link saved successfully:", newLink);
    } catch (error) {
      console.error("Error saving link:", error);
    }
  };

  const handleDeleteLink = async (linkToDelete) => {
    if (!linkToDelete || !linkToDelete.id) {
      console.error("Invalid linkToDelete:", linkToDelete);
      return;
    }

    try {
      const updatedLinks = linkedAssets.filter(
        (link) => link.id !== linkToDelete.id
      );

      if (isGroupView) {
        // Delete link from project group
        await projectGroupCollection
          .doc(id)
          .update({ linkedAssets: updatedLinks });
      } else {
        // Delete link from individual project
        await projectsCollection.doc(id).update({ linkedAssets: updatedLinks });
      }

      refetch(); // Refresh data after deletion
      console.log("Link deleted successfully:", linkToDelete);
    } catch (error) {
      console.error("Error deleting link:", error);
    }
  };

  return (
    <ProjectDetailsStyled>
      <ProjectHeader
        projectDetails={projectDetails}
        activeTab={activeTab}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        isSaving={isSaving}
        handleSave={handleSave}
        setIsAddLinkModalOpen={setIsAddLinkModalOpen}
        handleSaveLink={handleSaveLink}
        handleDeleteLink={handleDeleteLink}
        companyData={companyData}
      />

      <div>
        <div className="details-wrapper">
          <CustomTabs
            activeTab={activeTab}
            handleChange={setActiveTab}
            tabsList={PROJECT_DETAILS_TAB_LIST(isGroup)}
          />
        </div>
        {/* <hr></hr> */}

        <div className="tab-content">
          {activeTab === "basic-info" && (
            <BasicInfo
              form={form}
              isGroupView={isGroup}
              projectData={projectData}
              isEditMode={isEditMode}
              companyData={companyData}
              isGeneralUser={userPermissions === "General"}
            />
          )}
          {activeTab === "project-health" && (
            <ProjectHealthNew
              isGroupView={isGroup}
              materialsData={materialsData}
              companyData={companies}
            />
          )}
          {activeTab === "projects-assets" && (
            <ProjectAssets
              linkedAssets={linkedAssets}
              handleDeleteLink={handleDeleteLink}
            />
          )}
        </div>
        {isAddLinkModalOpen && (
          <AddLinkModal
            onClose={() => setIsAddLinkModalOpen(false)}
            onSave={handleSaveLink}
            projectName={projectDetails?.projectName}
            projectId={projectDetails?.id}
          />
        )}
        <div className="footer">
          <hr></hr>
          <div className="space-between">
            <div className="created">
              <Typography variant="body3">
                Created By: {projectData.createdBy}
              </Typography>
              <Typography variant="body3">
                Created Date: {projectData.dateCreated}
              </Typography>
            </div>

            {/* <div className="delete-icon">
              {!isGroup && !isGeneralOrPM && (
                <Button
                  variant="filled"
                  color={"error"}
                  endIcon={<DeleteIcon color="#fff" />}
                  onClick={() => deleteProject(id, isGroup)}
                >
                  Delete Project
                </Button>
              )}

              {isGroup && !isGeneralOrPM && (
                <Button
                  variant="filled"
                  color={"error"}
                  endIcon={<DeleteIcon color="#fff" />}
                  onClick={() => deleteProject(id, isGroup)}
                >
                  Delete Project Group
                </Button>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </ProjectDetailsStyled>
  );
};

export default ProjectDetails;
